import React, { createContext, useContext, useState, useEffect } from "react";
import CssBaseline from "@mui/material/CssBaseline";
import {
  ThemeProvider as MuiThemeProvider,
  useTheme,
} from "@mui/material/styles";
import GlobalStyles from "@mui/material/GlobalStyles";
import { createMuiTheme } from "./MUIThemeContext";
import { QueryClient, QueryClientProvider } from "react-query";
import { SnackbarProvider } from "notistack";
import { LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import { ProfileProvider } from "./ProfileContext";
import { LicenseInfo } from "@mui/x-license";
import { ConfirmProvider } from "material-ui-confirm";
import { NavProvider } from "./NavContext";
import { AuthProvider } from "../hooks/auth/useAuth";
import useAuth from "../hooks/auth/useAuth";
import usePushNotifications from "../hooks/push/usePushNotifications";
import useMessagingNotifyWebSocket from "../hooks/api/useMessagingNotifyWebSocket";

LicenseInfo.setLicenseKey(
  "be84dfa5d670faf9b906cae25317edf4Tz0xMTMwMDksRT0xNzg1NDU1OTk5MDAwLFM9cHJvLExNPXN1YnNjcmlwdGlvbixQVj1RMy0yMDI0LEtWPTI="
);

const ACTprepThemeContext = createContext();
const queryClient = new QueryClient();

export function useACTprepTheme() {
  return useContext(ACTprepThemeContext);
}

// Component to apply dynamic global styles
const DynamicGlobalStyles = () => {
  const theme = useTheme();

  return (
    <GlobalStyles
      styles={{
        ":root": {
          colorScheme: theme.palette.mode,
        },
      }}
    />
  );
};

const PushNotificationBootstrap = () => {
  const { token } = useAuth();
  usePushNotifications({ enabled: Boolean(token) });
  return null;
};

const MessagingNotificationsBootstrap = () => {
  const { token } = useAuth();
  useMessagingNotifyWebSocket({ enabled: Boolean(token) });
  return null;
};

export const ACTprepContextPackage = ({ children }) => {
  const [themeFlag, setThemeFlag] = useState("auto");

  const toggleTheme = (newThemeFlag) => {
    setThemeFlag(newThemeFlag);
    localStorage.setItem("themeFlag", newThemeFlag);
  };

  const getSystemTheme = () => {
    return window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light";
  };

  useEffect(() => {
    const savedThemeFlag = localStorage.getItem("themeFlag");
    if (savedThemeFlag) {
      setThemeFlag(savedThemeFlag);
    } else {
      setThemeFlag("auto");
    }
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = () => {
      if (themeFlag === "auto") {
        setThemeFlag(getSystemTheme());
      }
    };
    mediaQuery.addEventListener("change", handleChange);
    return () => {
      mediaQuery.removeEventListener("change", handleChange);
    };
  }, [themeFlag]);

  const selectedTheme = themeFlag === "auto" ? getSystemTheme() : themeFlag;

  const muiTheme = createMuiTheme(selectedTheme);

  return (
    <AuthProvider>
      <PushNotificationBootstrap />
      <MessagingNotificationsBootstrap />
      <QueryClientProvider client={queryClient}>
        <SnackbarProvider>
          <CssBaseline />
          <ProfileProvider>
            <ACTprepThemeContext.Provider
              value={{ theme: selectedTheme, themeFlag, toggleTheme }}
            >
              <MuiThemeProvider theme={muiTheme}>
                <DynamicGlobalStyles />
                <ConfirmProvider>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <NavProvider>{children}</NavProvider>
                  </LocalizationProvider>
                </ConfirmProvider>
              </MuiThemeProvider>
            </ACTprepThemeContext.Provider>
          </ProfileProvider>
        </SnackbarProvider>
      </QueryClientProvider>
    </AuthProvider>
  );
};
